<script setup lang="ts">
import CreateOrUpdateModal from "@/components/Department/CreateOrUpdateModal.vue"
import getDepartmentAllParams from "@/types/responses/getDepartmentAllParams"
import getOperationOfficeResponse from "@/types/responses/getOperationOffice"
import getPagenateDepartment from  "@/types/responses/getPagenateDepartment"
import DepartmentsList from "@/components/Department/DepartmentsList.vue"
import getDepartmentResponse from "@/types/responses/getDepartment"
import DeleteModal from "@/components/Department/DeleteModal.vue"
import SearchForm from "@/components/Department/SearchForm.vue"
import { useAuth } from "@/services/user/Auth0UserProvider"
import getDepartment from "@/types/requests/getDepartment"
import PaginateWithCount from "@/components/PaginateWithCount.vue"
import { Ref, ref, reactive, onMounted, watch } from "vue"
import { MANAGEMENT_OFFICE_TYPE, DEPARTMENT_OFFICE_TYPE } from "@/services/consts"
import SelectRoleChecker from "@/router/selectRoleChecker"
import { getAuthorities } from "@/services/functions"
import Api from "@/services/api/ApiServiceFabrick"
import getRole from '@/types/responses/getRole'
import SelfAccount from "@/types/SelfAccount"
import i18n from "@/lang/i18n"

const props = defineProps<{
  showFlash: (message: string, type: string, fadeout: boolean) => { value: any }
}>()

document.title = i18n.global.t('pages.departments.title')
const errors: Ref<any> = ref({})
const totalPage: Ref<number> = ref(0)
const itemsPerPage: Ref<number> = ref(15)
const allElementsCount: Ref<number> = ref(0)
const currentPage: Ref<number> = ref(1)
const { loginWithRedirect } = useAuth()
const departments: Ref<getDepartmentAllParams[]> = ref([])
const selectedDepartment: Ref<getDepartmentAllParams | null> = ref(null)
const isLoading: Ref<boolean> = ref(false)
const isSearchCompleted: Ref<boolean> = ref(false)
const createAuthority: Ref<boolean> = ref(false)
const updateAuthority: Ref<boolean> = ref(false)
const deleteAuthority: Ref<boolean> = ref(false)
const deleteModalStatus: Ref<boolean> = ref(false)
const createOrUpdateModalStatus: Ref<boolean> = ref(false)
const isManagementOffice: Ref<boolean> = ref(false)
const isDepartment: Ref<boolean> = ref(false)
const isChildDepartment: Ref<boolean> = ref(false)
const selfAccount = ref({} as SelfAccount)
const selfRole = ref({} as getRole)
const parentIdExistsDepartments: Ref<getDepartmentAllParams[]> = ref([])
const loginOrganizationId: Ref<number | undefined> = ref(undefined)
const searchParams: getDepartment = reactive({
  page: 1,
  operation_office_id: null,
  parent_id: null,
})

type formType = {
  operationOffice: getOperationOfficeResponse | null,
  parent: getDepartmentResponse | null
}

onMounted(async () => {
  getAuthorityList()
  getAllDepartments()
  await getSelf()
  if (!isManagementOffice.value) {
    getDepartments()
  }
})

async function getAuthorityList(isError = false) {
  const authorities = await getAuthorities(isError)
  createAuthority.value = authorities["octlink.department.create"]
  updateAuthority.value = authorities["octlink.department.update"]
  deleteAuthority.value = authorities["octlink.department.delete"]
}

function getDepartments() {
  isLoading.value = true
  Api().getDepartments(searchParams).then((data: getPagenateDepartment) => {
    departments.value = data.data
    currentPage.value = data.current_page
    totalPage.value = data.total_page
    allElementsCount.value = data.total
    itemsPerPage.value = data.per_page
    isLoading.value = false
    isSearchCompleted.value = true
  })
}

async function getSelf() {
  await Api().getSelfAccount().then((data) => {
    selfAccount.value = data
    isDepartment.value = data.login_organization_type == DEPARTMENT_OFFICE_TYPE
    isManagementOffice.value = data.login_organization_type == MANAGEMENT_OFFICE_TYPE
    loginOrganizationId.value = data.login_organization_id
    selfRole.value = SelectRoleChecker.getCurrentRole()
    if (isDepartment.value) isChildDepartment.value = !!selfRole.value?.organization.parent_id
  }).catch((error) => {
    return errorHandle(error.response)
  })
}

function getAllDepartments() {
  const params = {
    operation_office_id: null,
    parent_id: null,
  }
  Api().getDepartments(params).then((data: getPagenateDepartment) => {
    data.data.forEach((department: getDepartmentAllParams) => {
      if (department.parent_id) parentIdExistsDepartments.value.push(department)
    })
  })
}

function openModal(department: getDepartmentAllParams | null = null) {
  createOrUpdateModalStatus.value = true
  if (department) selectedDepartment.value = department
}

function openDeleteModal(department: getDepartmentAllParams) {
  deleteModalStatus.value = true
  selectedDepartment.value = department
}

function errorHandle (res: any) {
  switch (res.status) {
    case 401:
      loginWithRedirect({ appState: { targetUrl: location.pathname } })
      isLoading.value = false
      break
    case 403:
      props.showFlash(i18n.global.t('pages.common.messages.forbidden'), "error", true)
      isLoading.value = false
      break
    case 422:
      errors.value = res.data.errors
  }
  return isLoading.value = false
}

function searchDepartmentList(values: formType) {
  searchParams.page = 1
  searchParams.operation_office_id = values.operationOffice ? values.operationOffice.id : null
  searchParams.parent_id = values.parent ? values.parent.id : null
  getDepartments()
}

function closeModal(isReacquire: boolean) {
  selectedDepartment.value = null
  createOrUpdateModalStatus.value = false
  deleteModalStatus.value = false
  if (isReacquire) {
    searchParams.page = 1
    getDepartments()
  }
}

watch(currentPage, () => {
  searchParams.page = currentPage.value
  getDepartments()
})
</script>
<template>
  <div class="d-flex" style="min-height: 45vw;">
    <div class="content p-4 w-100">
      <div class="d-flex justify-content-between mb-1">
        <h4 class="card-title mt-0">
          {{ i18n.global.t('pages.departments.title') }}
        </h4>
      </div>
      <div class="mb-5 d-flex justify-end">
        <div class="btn-area">
          <v-btn
            v-if="createAuthority && !isChildDepartment"
            class="exec-btn mt-0"
            @click="openModal()"
          >
            {{ i18n.global.t('pages.common.button.newCreate') }}
          </v-btn>
        </div>
      </div>
      <div>
        <DepartmentsList
          class="mb-2"
          :departments="departments"
          :isLoading="isLoading"
          :deleteAuthority="deleteAuthority"
          :selfAccount="selfAccount"
          :isDepartment="isDepartment"
          :isManagementOffice="isManagementOffice"
          :isSearchCompleted="isSearchCompleted"
          :openModal="openModal"
          :deleteModal="openDeleteModal"
        />
        <PaginateWithCount
          class="justify-end"
          :totalPage="totalPage"
          v-model="currentPage"
          :itemsPerPage="itemsPerPage"
          :allElementsCount="allElementsCount"
          :showItemCount="true"
        />
      </div>
    </div>

    <SearchForm
      :isLoading="isLoading"
      :isChildDepartment="isChildDepartment"
      @search="searchDepartmentList"
    />
  </div>

  <CreateOrUpdateModal
    v-if="createOrUpdateModalStatus"
    v-model="createOrUpdateModalStatus"
    :parentIdExistsDepartments="parentIdExistsDepartments"
    :department="selectedDepartment"
    :isDepartment="isDepartment"
    :isChildDepartment="isChildDepartment"
    :loginOrganizationId="loginOrganizationId"
    :updateAuthority="updateAuthority"
    :closeModal="closeModal"
    :showFlash="props.showFlash"
  />
  <DeleteModal
    v-if="deleteModalStatus"
    v-model="deleteModalStatus"
    :parentIdExistsDepartments="parentIdExistsDepartments"
    :department="selectedDepartment"
    :closeModal="closeModal"
    :showFlash="props.showFlash"
  />
</template>
